import React, { useRef, useState } from "react";
import {
  Eye,
  EyeSlash,
  ArrowRight,
  ShieldCheck,
  Airplane,
  CreditCard2Front,
  Sun,
  Moon,
  Envelope,
} from "react-bootstrap-icons";
import { Link, useNavigate } from "react-router-dom";
import { notify } from "../../components/Common/Alerts";
import { decryptData } from "../../utils/crypto";
import Utils from "../../utils";
import { createlogin } from "../../services/Admin/authServices";
import { useDispatch } from "react-redux";
import { authActions } from "../../store";
import Logo from "../../assets/Logo1.png";

const Login = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let errorObj = {};

    if (!formData.email) {
      errorObj.email = "Email ID is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errorObj.email = "Please enter a valid email address";
    }

    // if (!formData.password) errorObj.password = "Password is required";

    const password = formData.password?.trim();

    if (!password) {
      errorObj.password = "Password is required";
    } else if (password.length < 6) {
      errorObj.password = "Password must be at least 8 characters";
    } else if (!/[A-Z]/.test(password)) {
      errorObj.password = "Must include at least 1 uppercase letter";
    } else if (!/[a-z]/.test(password)) {
      errorObj.password = "Must include at least 1 lowercase letter";
    } else if (!/[0-9]/.test(password)) {
      errorObj.password = "Must include at least 1 number";
    } else if (!/[^A-Za-z0-9]/.test(password)) {
      errorObj.password = "Must include at least 1 special character";
    }

    setErrors(errorObj);
    if (Object.keys(errorObj).length > 0) return;

    setIsLoading(true);

    let obj = {
      email: formData?.email || "",
      password: formData?.password,
      role_id: 2,
      // device_token: deviceToken,
      // device_type: deviceType,
    };

    setIsLoading(true);
    createlogin(obj)
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        // notify.success(decrypted.message);
        setIsLoading(true);
        let userData = decrypted?.user;
        if (userData) {
          Utils.setDetail(decrypted);
          Utils.setIntervalRemove();
          dispatch(authActions.login(decrypted));
          // navigate("/points-wallet");

          if (userData.has_user_points) {
            navigate("/dashboard-screen");
          } else {
            navigate("/points-wallet");
          }
        }
      })
      .catch((error) => {
        notify.error(Utils.getErrorMessage(error));
        setIsLoading(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div className={`${isDarkMode ? "dark" : ""}`}>
      <div className="min-h-screen bg-slate-50 dark:bg-[#060B16] flex flex-col lg:flex-row overflow-x-hidden transition-colors duration-300">
        {/* TOP CONTAINER FOR MOBILE LOGO & TOGGLE */}
        <div className="w-full lg:hidden flex items-center justify-between px-5 pt-6 pb-2 relative z-50">
          <div className="flex items-center" onClick={() => navigate("/")}>
            <div className="p-2 rounded-xl">
              <img src={Logo} alt="Logo" className="w-10 h-10 object-contain" />
            </div>
            <h2 className="text-slate-900 dark:text-white text-xl font-black tracking-tight">
              JustFor<span className="text-voyage-cyan">Points</span>
            </h2>
          </div>

          <button
            type="button"
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="p-2.5 rounded-xl border bg-white text-slate-700 border-slate-200 hover:bg-slate-50 dark:bg-white/5 dark:text-gray-300 dark:border-white/10 dark:hover:bg-white/10 transition-all shadow-sm"
          >
            {isDarkMode ? (
              <Sun size={18} className="text-voyage-cyan" />
            ) : (
              <Moon size={18} />
            )}
          </button>
        </div>

        <div className="hidden lg:flex w-1/2 relative overflow-hidden items-center justify-center bg-gradient-to-br from-slate-100 via-slate-200 to-white dark:from-[#0B1220] dark:via-[#111827] dark:to-black transition-colors duration-300">
          <div className="absolute top-[-100px] left-[-100px] w-[400px] h-[400px] bg-voyage-cyan/10 rounded-full blur-3xl opacity-60 dark:opacity-100" />
          <div className="absolute bottom-[-120px] right-[-120px] w-[420px] h-[420px] bg-voyage-cyan/5 rounded-full blur-3xl opacity-60 dark:opacity-100" />

          <div className="relative z-10 max-w-xl px-12">
            <div className="mb-10">
              <div className="flex items-center mb-8">
                <div className="p-3 rounded-2xl">
                  <img
                    src={Logo}
                    alt="Logo"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h1 className="text-4xl font-black text-slate-900 dark:text-white tracking-tight">
                  JustFor<span className="text-voyage-cyan">Points</span>
                </h1>
              </div>

              <h2 className="text-5xl leading-tight font-black text-slate-900 dark:text-white mb-6">
                Travel More. <br />
                <span className="text-voyage-cyan">Earn Bigger.</span>
              </h2>

              <p className="text-slate-600 dark:text-gray-300 text-lg leading-relaxed max-w-lg">
                Turn your reward points into luxury flights, premium hotel
                stays, and elite memberships effortlessly.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-5 mt-12">
              <FeatureCard
                icon={<Airplane size={30} />}
                title="Flight Rewards"
                desc="Book international flights using points instantly."
              />
              <FeatureCard
                icon={<CreditCard2Front size={30} />}
                title="Card Benefits"
                desc="Maximize Amex, Chase, and travel partner rewards."
              />
            </div>

            <div className="flex gap-10 mt-12">
              <Stat value="250K+" label="Flights Booked" />
              <Stat value="$12M+" label="Redeemed" />
              <Stat value="4.9★" label="User Rating" />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setIsDarkMode(!isDarkMode)}
            className="absolute top-6 right-6 z-50 p-2.5 rounded-xl border bg-white text-slate-700 border-slate-200 hover:bg-slate-50 dark:bg-white/5 dark:text-gray-300 dark:border-white/10 dark:hover:bg-white/10 transition-all shadow-sm"
          >
            {isDarkMode ? (
              <Sun size={20} className="text-voyage-cyan" />
            ) : (
              <Moon size={20} />
            )}
          </button>
        </div>

        <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-8 lg:px-16 xl:px-24 py-6 relative">
          <div className="w-full max-w-md mx-auto">
            {/* Form Box - Enhanced Responsive Spacing and Sizing */}
            <div className="bg-white border border-slate-200 dark:bg-white/5 dark:border-white/10 backdrop-blur-2xl rounded-[2rem] sm:rounded-[2.5rem] p-6 sm:p-10 shadow-xl dark:shadow-2xl transition-all duration-300">
              <div className="mb-8 text-center lg:text-left">
                <h2 className="text-2xl sm:text-4xl font-black text-slate-900 dark:text-white mb-2">
                  Welcome Back
                </h2>
                <p className="text-slate-500 dark:text-gray-400 text-xs sm:text-base">
                  Login to access your rewards dashboard and travel benefits.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
                <div className="space-y-1.5">
                  <label className="text-xs sm:text-sm font-bold text-slate-700 dark:text-gray-300 ml-1">
                    Email
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      ref={emailRef}
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email address"
                      className="w-full bg-slate-50 text-slate-900 border border-slate-200 dark:bg-white/5 dark:text-white dark:border-white/10 rounded-xl sm:rounded-2xl px-4 sm:px-5 py-3.5 sm:py-4 placeholder:text-slate-400 dark:placeholder:text-gray-500 focus:border-voyage-cyan focus:ring-4 focus:ring-voyage-cyan/10 transition-all outline-none text-sm sm:text-base"
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-500 dark:text-red-400 text-xs mt-1 ml-1">
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs sm:text-sm font-bold text-slate-700 dark:text-gray-300 ml-1">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      ref={passwordRef}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter your password"
                      className="w-full bg-slate-50 text-slate-900 border border-slate-200 dark:bg-white/5 dark:text-white dark:border-white/10 rounded-xl sm:rounded-2xl px-4 sm:px-5 py-3.5 sm:py-4 pr-12 placeholder:text-slate-400 dark:placeholder:text-gray-500 focus:border-voyage-cyan focus:ring-4 focus:ring-voyage-cyan/10 transition-all outline-none text-sm sm:text-base"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-voyage-cyan dark:text-gray-500 dark:hover:text-voyage-cyan transition-colors"
                    >
                      {showPassword ? (
                        <EyeSlash size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 dark:text-red-400 text-xs mt-1 ml-1">
                      {errors.password}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  {/* <button
                    type="button"
                    className="text-xs sm:text-sm font-bold text-voyage-cyan hover:brightness-90 dark:hover:brightness-110 transition-all"
                  >
                    Forgot Password?
                  </button> */}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-voyage-cyan text-slate-900 dark:text-slate-950 font-black py-3.5 sm:py-4 rounded-xl sm:rounded-2xl text-base sm:text-lg shadow-xl shadow-voyage-cyan/25 hover:scale-[1.01] active:scale-[0.99] transition-all flex items-center justify-center gap-2.5 disabled:opacity-70 disabled:hover:scale-100 mt-2"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-slate-900/20 border-t-slate-900 rounded-full animate-spin" />
                      <span>Signing In...</span>
                    </div>
                  ) : (
                    <>
                      Login
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>
              </form>

              <div className="mt-5 text-center">
                <p className="text-xs sm:text-sm text-slate-500 dark:text-gray-400">
                  Don't have an account?
                  <Link
                    to="/sign-up"
                    className="text-voyage-cyan font-bold hover:underline ml-1 transition-colors"
                  >
                    Register
                  </Link>
                </p>
              </div>

              <div className="mt-6 pt-5 border-t border-slate-100 dark:border-white/10 flex items-center justify-center gap-2 text-slate-400 dark:text-gray-500 text-[11px] sm:text-xs">
                <ShieldCheck className="text-voyage-cyan" size={15} />
                Enterprise-grade secure access
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-white/60 border border-slate-200/80 dark:bg-white/5 dark:border-white/10 backdrop-blur-xl rounded-3xl p-5 hover:bg-white dark:hover:bg-white/10 transition-all duration-200 cursor-default shadow-sm dark:shadow-none">
    <div className="text-voyage-cyan mb-4">{icon}</div>
    <h3 className="text-slate-900 dark:text-white font-bold text-lg mb-1">
      {title}
    </h3>
    <p className="text-slate-500 dark:text-gray-400 text-sm leading-snug">
      {desc}
    </p>
  </div>
);

const Stat = ({ value, label }) => (
  <div>
    <h4 className="text-3xl font-black text-voyage-cyan">{value}</h4>
    <p className="text-slate-500 dark:text-gray-400 text-xs uppercase tracking-wider font-bold mt-1">
      {label}
    </p>
  </div>
);

export default Login;
